/**
 * Chip — iter-213.5
 *
 * Pill-shaped button primitive used for filter chips, toggle badges,
 * removable tags, and any other "small selectable token" UX. Unlike
 * the shadcn `<Button>` it does NOT force `[&_svg]:size-4` on
 * children, so callers can render arbitrarily small icons
 * (e.g. `h-2.5 w-2.5` for filter chips that have a leading dot).
 *
 * Variants
 * --------
 *   default   — idle (slate background)
 *   selected  — actively applied filter (slate-900 background)
 *   ghost     — bordered transparent (used in toolbars)
 *
 * Tones (override variant color when not "selected")
 * --------------------------------------------------
 *   default | rose | amber | emerald | sky | purple
 *
 * Sizes
 * -----
 *   sm  — `text-[10px] px-2 py-0.5` (the default; matches filter chips)
 *   md  — `text-xs px-2.5 py-1`
 *
 * Examples
 * --------
 *   <Chip onClick={() => setActive(!active)} selected={active}>
 *     <Filter className="h-2.5 w-2.5" /> Mis filtros
 *   </Chip>
 *
 *   <Chip tone="emerald">Paid</Chip>
 */
import React from "react";
import { cn } from "../../lib/utils";

const TONE_CLASSES = {
  default: "bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200",
  rose: "bg-rose-50 text-rose-700 hover:bg-rose-100 border-rose-200",
  amber: "bg-amber-50 text-amber-800 hover:bg-amber-100 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-200",
  sky: "bg-sky-50 text-sky-700 hover:bg-sky-100 border-sky-200",
  purple: "bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200",
};

const SIZE_CLASSES = {
  sm: "text-[10px] px-2 py-0.5",
  md: "text-xs px-2.5 py-1",
};

export const Chip = React.forwardRef(function Chip(
  {
    children,
    selected = false,
    tone = "default",
    size = "sm",
    onClick,
    className,
    asChild,
    type = "button",
    disabled = false,
    "data-testid": dataTestId,
    ...rest
  },
  ref,
) {
  const toneCls = selected
    ? "bg-slate-900 text-white hover:bg-slate-800 border-slate-900"
    : TONE_CLASSES[tone] || TONE_CLASSES.default;
  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const base = cn(
    "inline-flex items-center gap-1 rounded-full border font-semibold uppercase tracking-wider",
    "transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    sizeCls,
    toneCls,
    className,
  );
  // asChild is rarely needed but supports wrapping Link or NavLink
  // without losing the styling — same escape hatch shadcn uses.
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      className: cn(base, children.props.className),
      onClick,
      "data-testid": dataTestId,
      ref,
      ...rest,
    });
  }
  return (
    <button
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={base}
      data-testid={dataTestId}
      {...rest}
    >
      {children}
    </button>
  );
});
